import type { VectorizeResult, VectorPath } from './engine';

/** Small, browser-independent SVG path model used by the Path Editor. */
export type EditorPoint = { x: number; y: number };
export type EditorNode = { id: string; point: EditorPoint; inHandle?: EditorPoint; outHandle?: EditorPoint };
export type EditorSubpath = { nodes: EditorNode[]; closed: boolean };
export type EditablePath = { id: string; fill: string; area: number; fillRule?: 'evenodd' | 'nonzero'; subpaths: EditorSubpath[] };

/** Expand explicit anchors to their connected contours, without crossing color seams. */
export function selectConnectedEditableNodes(paths: EditablePath[], selection: ReadonlySet<string>): Set<string> {
  const next = new Set<string>();
  for (const path of paths) for (const subpath of path.subpaths) {
    if (subpath.nodes.some(node => selection.has(node.id))) for (const node of subpath.nodes) next.add(node.id);
  }
  return next;
}

type Token = { command?: string; value?: number };
const tokenize = (d: string): Token[] => {
  const tokens: Token[] = [];
  const re = /([a-zA-Z])|([-+]?(?:\d*\.\d+|\d+\.?)(?:e[-+]?\d+)?)/gi;
  for (const match of d.matchAll(re)) tokens.push(match[1] ? { command: match[1] } : { value: Number(match[2]) });
  return tokens;
};
const clone = (p: EditorPoint): EditorPoint => ({ x: p.x, y: p.y });
const point = (x: number, y: number): EditorPoint => ({ x, y });

/** Parse the M/L/C/Z dialect emitted by the vector tracing engines. */
export function parseEditablePath(path: { id: string; fill: string; area: number; fillRule?: 'evenodd' | 'nonzero'; d: string }): EditablePath {
  if (/[^mlcz0-9eE+.,\s-]/i.test(path.d) || (path.d.match(/[a-df-z]/gi) ?? []).some(c => !/[mlcz]/i.test(c))) throw new Error('Unsupported editor path command');
  const tokens = tokenize(path.d), subpaths: EditorSubpath[] = [];
  let command = '', cursor = point(0, 0), start = point(0, 0), current: EditorSubpath | null = null;
  let index = 0;
  const read = () => {
    const value = tokens[index]?.value; if (value === undefined) return undefined; index++; return value;
  };
  const pushMove = (x: number, y: number) => {
    const next: EditorSubpath = { nodes: [{ id: `${path.id}-${subpaths.length}-0`, point: point(x, y) }], closed: false };
    subpaths.push(next); cursor = point(x, y); start = point(x, y); return next;
  };
  while (index < tokens.length) {
    if (tokens[index].command) command = tokens[index++].command!;
    const relative = command === command.toLowerCase();
    const upper = command.toUpperCase();
    if (upper === 'Z') { if (current) current.closed = true; cursor = clone(start); command = ''; continue; }
    const x = read(); const y = read(); if (x === undefined || y === undefined) break;
    const abs = (v: number, base: number) => relative ? v + base : v;
    if (upper === 'M') {
      current = pushMove(abs(x, cursor.x), abs(y, cursor.y));
      // Subsequent coordinate pairs after M are implicit L commands.
      command = relative ? 'l' : 'L';
      continue;
    }
    if (!current) { command = ''; continue; }
    if (upper === 'L') {
      const end = point(abs(x, cursor.x), abs(y, cursor.y));
      current.nodes.push({ id: `${path.id}-${subpaths.length - 1}-${current.nodes.length}`, point: end }); cursor = end; continue;
    }
    if (upper === 'C') {
      const c2x = read(), c2y = read(), ex = read(), ey = read();
      if ([c2x, c2y, ex, ey].some(value => value === undefined)) break;
      const c1 = point(abs(x, cursor.x), abs(y, cursor.y));
      const c2 = point(abs(c2x!, cursor.x), abs(c2y!, cursor.y));
      const end = point(abs(ex!, cursor.x), abs(ey!, cursor.y));
      const previous = current.nodes[current.nodes.length - 1]; previous.outHandle = c1;
      current.nodes.push({ id: `${path.id}-${subpaths.length - 1}-${current.nodes.length}`, point: end, inHandle: c2 }); cursor = end; continue;
    }
    throw new Error('Unsupported editor path command');
  }
  // Tracers explicitly end the final cubic at M before appending Z. Treat
  // that coordinate as the first anchor, not as a second coincident point.
  for (const subpath of subpaths) if (subpath.closed && subpath.nodes.length > 1) {
    const first = subpath.nodes[0], last = subpath.nodes[subpath.nodes.length - 1];
    if (same(first.point, last.point)) { if (last.inHandle) first.inHandle = last.inHandle; subpath.nodes.pop(); }
  }
  return { id: path.id, fill: path.fill, area: path.area, fillRule: path.fillRule, subpaths };
}

// Keep the source's exact precision, including subpixel polygon fallbacks.
// Rounding every path after editing one anchor would move unrelated geometry.
const fmt = (value: number) => String(Object.is(value, -0) ? 0 : value);
const pair = (p: EditorPoint) => `${fmt(p.x)} ${fmt(p.y)}`;
const same = (a?: EditorPoint, b?: EditorPoint) => Boolean(a && b && Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9);

// Editor documents are immutable. Weak caches avoid reserializing untouched
// contours during a drag or a requestAnimationFrame keyboard movement.
const pathSerialization = new WeakMap<EditablePath, string>();
const subpathSerialization = new WeakMap<EditorSubpath, string>();
function serializeSubpath(subpath: EditorSubpath): string {
  const cached = subpathSerialization.get(subpath);
  if (cached !== undefined) return cached;
  let d = '';
  if (subpath.nodes.length) {
    d += `M${pair(subpath.nodes[0].point)}`;
    for (let i = 1; i < subpath.nodes.length; i++) d += segment(subpath.nodes[i - 1], subpath.nodes[i]);
    // Z already draws a straight closing edge. A redundant L can change
    // antialias tessellation despite identical geometric coverage.
    if (subpath.closed && (subpath.nodes[subpath.nodes.length - 1].outHandle || subpath.nodes[0].inHandle)) d += segment(subpath.nodes[subpath.nodes.length - 1], subpath.nodes[0]);
    if (subpath.closed) d += 'Z';
  }
  subpathSerialization.set(subpath, d);
  return d;
}
export function serializeEditablePath(path: EditablePath): string {
  const cached = pathSerialization.get(path);
  if (cached !== undefined) return cached;
  const d = path.subpaths.map(serializeSubpath).join('');
  pathSerialization.set(path, d); return d;
}
function segment(a: EditorNode, b: EditorNode) {
  const c1 = a.outHandle, c2 = b.inHandle;
  return c1 || c2 ? `C${pair(c1 ?? a.point)} ${pair(c2 ?? b.point)} ${pair(b.point)}` : `L${pair(b.point)}`;
}

type SerializedEditorPath = { vector: ReturnType<typeof createVectorPath>; pointCount: number; fragment: string };
const vectorSerialization = new WeakMap<EditablePath, SerializedEditorPath>();
function createVectorPath(path: EditablePath) {
  return { id: path.id, fill: path.fill, area: path.area, fillRule: path.fillRule, d: serializeEditablePath(path) };
}
function serializedEditorPath(path: EditablePath): SerializedEditorPath {
  const cached = vectorSerialization.get(path); if (cached) return cached;
  const vector = createVectorPath(path), pointCount = (vector.d.match(/[-+]?(?:\d*\.\d+|\d+\.?)(?:e[-+]?\d+)?/gi)?.length ?? 0) / 2;
  const fragment = vector.d ? `<path id="${xmlAttribute(path.id)}" fill="${xmlAttribute(path.fill)}" fill-rule="${path.fillRule === 'nonzero' ? 'nonzero' : 'evenodd'}" d="${vector.d}"/>` : '';
  const value = { vector, pointCount, fragment }; vectorSerialization.set(path, value); return value;
}
export function editablePathToVectorPath(path: EditablePath) {
  return serializedEditorPath(path).vector;
}
const xmlAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/'/g, '&apos;');
/** One serialization supplies both the rendered vector and the downloaded SVG. */
export function editablePathsToResult(paths: EditablePath[], base: VectorizeResult): VectorizeResult {
  const serialized = paths.map(serializedEditorPath).filter(path => path.vector.d.length > 0), vectorPaths = serialized.map(path => path.vector);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${base.width}" height="${base.height}" viewBox="0 0 ${base.width} ${base.height}"><title>DesignForge — Image Trace Promax</title>${serialized.map(path => path.fragment).join('')}</svg>`;
  return { ...base, paths: vectorPaths, svg, palette: [...new Set(vectorPaths.map(path => path.fill))], pointCount: serialized.reduce((sum, path) => sum + path.pointCount, 0) };
}
export function editableAnchorCount(path: EditablePath) { return path.subpaths.reduce((sum, subpath) => sum + subpath.nodes.length, 0); }

const anchorCounts = new WeakMap<VectorPath, number>();
/** Exclude handles and the coincident closing endpoint from the preview count. */
export function vectorPathAnchorCount(path: VectorPath) {
  const known = anchorCounts.get(path); if (known !== undefined) return known;
  const count = editableAnchorCount(parseEditablePath(path));
  anchorCounts.set(path, count); return count;
}

function mapEditableNodes(path: EditablePath, update: (node: EditorNode) => EditorNode): EditablePath {
  let subpaths: EditorSubpath[] | null = null;
  for (let s = 0; s < path.subpaths.length; s++) {
    const subpath = path.subpaths[s]; let nodes: EditorNode[] | null = null;
    for (let i = 0; i < subpath.nodes.length; i++) {
      const node = subpath.nodes[i], next = update(node);
      if (next === node) continue;
      nodes ??= subpath.nodes.slice(); nodes[i] = next;
    }
    if (!nodes) continue;
    subpaths ??= path.subpaths.slice(); subpaths[s] = { ...subpath, nodes };
  }
  return subpaths ? { ...path, subpaths } : path;
}
function mapEditablePaths(paths: EditablePath[], update: (path: EditablePath) => EditablePath): EditablePath[] {
  let next: EditablePath[] | null = null;
  for (let i = 0; i < paths.length; i++) {
    const changed = update(paths[i]); if (changed === paths[i]) continue;
    next ??= paths.slice(); next[i] = changed;
  }
  return next ?? paths;
}

export function moveEditableNodes(path: EditablePath, selected: ReadonlySet<string>, dx: number, dy: number): EditablePath {
  if ((!dx && !dy) || !selected.size) return path;
  return mapEditableNodes(path, node => selected.has(node.id) ? {
    ...node, point: point(node.point.x + dx, node.point.y + dy),
    inHandle: node.inHandle && point(node.inHandle.x + dx, node.inHandle.y + dy), outHandle: node.outHandle && point(node.outHandle.x + dx, node.outHandle.y + dy),
  } : node);
}
export function moveEditableHandle(path: EditablePath, nodeId: string, handle: 'inHandle' | 'outHandle', x: number, y: number): EditablePath {
  return mapEditableNodes(path, node => node.id === nodeId && (node[handle]?.x !== x || node[handle]?.y !== y) ? { ...node, [handle]: point(x, y) } : node);
}

export function deleteEditableNodes(path: EditablePath, selected: ReadonlySet<string>): EditablePath {
  if (!selected.size) return path;
  let changed = false;
  const subpaths = path.subpaths.flatMap(subpath => {
    const nodes = subpath.nodes.filter(node => !selected.has(node.id));
    if (nodes.length === subpath.nodes.length) return [subpath];
    changed = true;
    const minimum = subpath.closed && !nodes.some(node => node.inHandle || node.outHandle) ? 3 : 2;
    if (nodes.length < minimum) return [];
    return [{ ...subpath, nodes }];
  });
  return changed ? { ...path, subpaths } : path;
}

const mix = (a: EditorPoint, b: EditorPoint, t: number) => point(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);

/** Insert an anchor while preserving the selected cubic/line segment exactly. */
export function insertEditableNode(path: EditablePath, subpathIndex: number, segmentIndex: number, t = .5): EditablePath {
  const subpath = path.subpaths[subpathIndex]; if (!subpath || !subpath.nodes.length) return path;
  if (!Number.isInteger(segmentIndex) || segmentIndex < 0 || segmentIndex >= subpath.nodes.length || !Number.isFinite(t)) return path;
  const clamped = Math.max(0, Math.min(1, t)), nextIndex = (segmentIndex + 1) % subpath.nodes.length;
  if (!subpath.closed && nextIndex === 0) return path;
  const a = subpath.nodes[segmentIndex], b = subpath.nodes[nextIndex];
  const c1 = a.outHandle ?? a.point, c2 = b.inHandle ?? b.point;
  const ab = mix(a.point, c1, clamped), bc = mix(c1, c2, clamped), cd = mix(c2, b.point, clamped), abc = mix(ab, bc, clamped), bcd = mix(bc, cd, clamped);
  const cubicSegment = Boolean(a.outHandle || b.inHandle);
  const mid = cubicSegment ? mix(abc, bcd, clamped) : mix(a.point, b.point, clamped);
  const left: EditorNode = { ...a, outHandle: cubicSegment ? ab : undefined };
  const right: EditorNode = { ...b, inHandle: cubicSegment ? cd : undefined };
  let serial = 0;
  const used = new Set(path.subpaths.flatMap(value => value.nodes.map(value => value.id)));
  while (used.has(`${path.id}-${subpathIndex}-insert-${serial}`)) serial++;
  const node: EditorNode = { id: `${path.id}-${subpathIndex}-insert-${serial}`, point: mid, ...(cubicSegment ? { inHandle: abc, outHandle: bcd } : {}) };
  const nodes = subpath.nodes.slice(); nodes[segmentIndex] = left;
  if (subpath.closed && nextIndex === 0) nodes.push(node); else nodes.splice(segmentIndex + 1, 0, node);
  if (subpath.closed && nextIndex === 0) { nodes[0] = right; }
  else { const rightIndex = segmentIndex + 2; if (nodes[rightIndex]) nodes[rightIndex] = right; }
  const subpaths = path.subpaths.slice(); subpaths[subpathIndex] = { ...subpath, nodes }; return { ...path, subpaths };
}

const pointKey = (p: EditorPoint) => `${Math.round(p.x * 1e9)},${Math.round(p.y * 1e9)}`;
/** Exact copies in color suffix layers form one editable junction, not seams. */
export function linkedEditableSelection(paths: EditablePath[], selected: ReadonlySet<string>): Set<string> {
  const coordinates = new Set<string>();
  for (const path of paths) for (const subpath of path.subpaths) for (const node of subpath.nodes) if (selected.has(node.id)) coordinates.add(pointKey(node.point));
  const linked = new Set(selected);
  for (const path of paths) for (const subpath of path.subpaths) for (const node of subpath.nodes) if (coordinates.has(pointKey(node.point))) linked.add(node.id);
  return linked;
}
export function moveLinkedEditableNodes(paths: EditablePath[], selected: ReadonlySet<string>, dx: number, dy: number): EditablePath[] {
  if ((!dx && !dy) || !selected.size) return paths;
  const linked = linkedEditableSelection(paths, selected);
  return mapEditablePaths(paths, path => moveEditableNodes(path, linked, dx, dy));
}
export function deleteLinkedEditableNodes(paths: EditablePath[], selected: ReadonlySet<string>): EditablePath[] {
  const linked = linkedEditableSelection(paths, selected);
  return mapEditablePaths(paths, path => deleteEditableNodes(path, linked));
}
export function moveLinkedEditableHandle(paths: EditablePath[], nodeId: string, handle: 'inHandle' | 'outHandle', x: number, y: number): EditablePath[] {
  const controlsAt = (subpath: EditorSubpath, index: number) => {
    if (index < 0 || index >= subpath.nodes.length || !subpath.closed && index === subpath.nodes.length - 1) return undefined;
    const a = subpath.nodes[index], b = subpath.nodes[(index + 1) % subpath.nodes.length];
    return [a.point, a.outHandle ?? a.point, b.inHandle ?? b.point, b.point];
  };
  let sourceControls: EditorPoint[] | undefined, sourceFound = false;
  for (const path of paths) { for (const subpath of path.subpaths) {
    const index = subpath.nodes.findIndex(node => node.id === nodeId); if (index < 0) continue;
    sourceFound = true;
    sourceControls = controlsAt(subpath, handle === 'outHandle' ? index : index > 0 ? index - 1 : subpath.closed ? subpath.nodes.length - 1 : -1);
    break;
  } if (sourceFound) break; }
  if (!sourceFound) return paths;
  const linked = new Map<string, Set<'inHandle' | 'outHandle'>>([[nodeId, new Set([handle])]]);
  const link = (id: string, side: 'inHandle' | 'outHandle') => { const sides = linked.get(id) ?? new Set(); sides.add(side); linked.set(id, sides); };
  if (sourceControls) for (const path of paths) for (const subpath of path.subpaths) for (let i = 0; i < subpath.nodes.length - (subpath.closed ? 0 : 1); i++) {
    const candidate = controlsAt(subpath, i)!;
    const forward = sourceControls.every((p, index) => same(p, candidate[index]));
    const reverse = !forward && sourceControls.every((p, index) => same(p, candidate[3 - index]));
    if (!forward && !reverse) continue;
    // Link the exact edge's corresponding control. Coincident cusp controls
    // on the other side of the same anchor are independent handles.
    const startHandle = forward ? handle === 'outHandle' : handle === 'inHandle';
    link(subpath.nodes[startHandle ? i : (i + 1) % subpath.nodes.length].id, startHandle ? 'outHandle' : 'inHandle');
  }
  return mapEditablePaths(paths, path => mapEditableNodes(path, node => {
    const sides = linked.get(node.id); if (!sides) return node;
    let next = node;
    for (const side of sides) if (node[side]?.x !== x || node[side]?.y !== y) next = { ...next, [side]: point(x, y) };
    return next;
  }));
}

/** Split every exact copy of the selected edge, including reversed neighbors. */
export function insertLinkedEditableNode(paths: EditablePath[], pathId: string, subpathIndex: number, segmentIndex: number, t = .5): EditablePath[] {
  const source = paths.find(path => path.id === pathId)?.subpaths[subpathIndex];
  if (!source || !source.nodes[segmentIndex] || (!source.closed && segmentIndex === source.nodes.length - 1)) return paths;
  const a = source.nodes[segmentIndex], b = source.nodes[(segmentIndex + 1) % source.nodes.length];
  const controls = [a.point, a.outHandle ?? a.point, b.inHandle ?? b.point, b.point];
  return paths.map(path => {
    const matches: { subpath: number; segment: number; t: number }[] = [];
    for (let s = 0; s < path.subpaths.length; s++) {
      const subpath = path.subpaths[s];
      for (let i = 0; i < subpath.nodes.length - (subpath.closed ? 0 : 1); i++) {
        const left = subpath.nodes[i], right = subpath.nodes[(i + 1) % subpath.nodes.length];
        const candidate = [left.point, left.outHandle ?? left.point, right.inHandle ?? right.point, right.point];
        if (controls.every((p, index) => same(p, candidate[index]))) matches.push({ subpath: s, segment: i, t });
        else if (controls.every((p, index) => same(p, candidate[3 - index]))) matches.push({ subpath: s, segment: i, t: 1 - t });
      }
    }
    // Descending indexes keep the original segment indexes stable.
    matches.sort((a, b) => b.subpath - a.subpath || b.segment - a.segment);
    return matches.reduce((current, match) => insertEditableNode(current, match.subpath, match.segment, match.t), path);
  });
}
